import React from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import lround from "lodash/round";
import submit from "submit-form";
import convert from "convert-units";
const {
  PARAMETER_OPTIONS: { FREEFORM, LOAD, RPEAMRAP, REPS },
  RPE_RANGE,
} = require("../../../../constants");
import { mapParameter } from "../../../../parts/other-exercise/CustomParametersSets";
import ParameterQuickEditInput from "../../../../parts/inputs/parameter-quick-edit-input";
import NewExerciseLineParameters from "./NewExerciseLineParametersPe";
import convertToRiR from "../../../../utils/converters/convert-intensity";
import { repsValue } from "../../../../utils/reps-range";
import { getExerciseName } from "../../../../parts/modals/learn-more/blockHelpers";
import { getMovementsData } from "../../../../utils/exercise-library";
import { SearchDropdown } from "../../../../parts/inputs/search-dropdown";
import SvgIcon from "../../../../parts/SvgIcon";
import { CopyExerciseComponent } from "../../../../parts/CopyExerciseComponent";
import { getLoadDrop } from "../../../../utils/load-drop";

const EditorExerciseLineItem = (
  {
    handleQuickEditChange,
    _handleOpenAddSet,
    _handleCloseAddSet,
    _handleNewSetChange,
    _handleSaveNewSet,
    _handleNewMovementChange,
    newSet,
    setIndex,
    activeExercise,
    fromMetric,
    activeUser,
    withWeight,
    loadInputs,
    intensityInputs,
    localLoadInputs,
    localIntensityInputs,
    isQuickEditOpen,
    isAddingSet,
    currentRow,
    units,
    setLength,
    activeUserToRequest,
    set,
    deleteExercise,
    updateExercise,
    isProgramEditor,
    handleDeleteSetInState,
    _handleSaveNewSetForOther,
    OrderArrowsComponent,
    prefix,
    OptionsComponent = null,
    isCoach,
    activeUserToHandleCopyAction,
  },
  { locals }
) => {
  const isAssistant = activeExercise.type === "assistant";
  const isPrimary = activeExercise.type === "primary";
  // gets primary repmax of the exercise if exercise type is primary else
  // if secondary, use secondary rm, else if assistant just use active user's squat (RM doesn't really matter for assistant)
  // if all else fails, default to 0
  let defaultRepMax = get(
    activeExercise,
    `${
      isPrimary
        ? `repMaximums.${activeExercise.primary}`
        : "secondaryRepMaximum"
    }`,
    get(activeUser, "repMaximums.squat", 0)
  );

  if (activeExercise.type == "secondary") {
    const movement =
      activeUserToRequest.exerciseLibrary[activeExercise.primary]?.find(
        (m) => m.movement === activeExercise.secondary
      ) || {};
    defaultRepMax = movement.repMaximum || defaultRepMax;
  }

  defaultRepMax = Math.round(fromMetric(defaultRepMax, units), 0);
  const precision = units === "metric" ? 2 : 1;
  const load = set.load ? lround(fromMetric(set.load, units), precision) : 0;

  const exerciseName = getExerciseName(activeExercise);

  let tabIndex = 100;
  const hasLoad = Boolean(load);
  const exId = activeExercise._id;
  const handleOpenAddSet = (e) => _handleOpenAddSet(e, setIndex);
  const handleDeleteSet = (e) => {
    e.preventDefault();
    const copyActiveExercise = cloneDeep(activeExercise);

    if (isProgramEditor) {
      if (activeExercise.sets.length === 1) {
        deleteExercise(activeExercise._id);
      } else {
        // Update for optimistic optimizations
        copyActiveExercise.sets.splice(setIndex, 1);
        updateExercise(copyActiveExercise);

        // update exercise in state (the one send to the api if clicking on save)
        handleDeleteSetInState(setIndex);
      }
    }

    let organizationCoach = undefined;
    if (activeUser.organizationId || activeUser?.coach?.organizationId) {
      organizationCoach = activeUserToRequest._id;
    }

    const actionToRequest = activeUser?.permissions?.includes("coach")
      ? `/actions/exercise/quick-edit/sets/remove/${setIndex}/${exId}/${organizationCoach}/${undefined}`
      : `/actions/exercise/quick-edit/sets/remove/${setIndex}/${exId}/${organizationCoach}/${activeUser._id}`;

    submit(actionToRequest, {
      method: isProgramEditor ? "POST" : "GET",
      body: {
        omitModified: true,
        omitActiveUser: true,
        editExercise: activeExercise,
      },
    });
  };
  // used to select the first field when adding set
  const handleFocus = (e) => {
    e.target.select();
  };

  const movements = getMovementsData(
    activeExercise,
    activeUserToRequest,
    locals.athlete || activeUserToRequest
  );

  const intensityMeasure =
    get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";
  const isRiR = intensityMeasure === "RiR";
  const amrapLabel = isRiR ? "RiR" : "RPE";
  const inputClassName = isQuickEditOpen ? "" : "hidden";
  const valueClassName = isQuickEditOpen ? "hidden" : "";
  const isFreeForm = activeExercise.parameters?.[0] === FREEFORM;

  return (
    <>
      {setIndex === 0 && (
        <>
          <div className="name-wrapper">
            <span className="calendar-prefix">{prefix}</span>
            {isQuickEditOpen ? (
              <SearchDropdown
                valueKey="movementLabel"
                options={movements}
                handleChange={(val) =>
                  _handleNewMovementChange(val, exId, setIndex)
                }
                placeholder={exerciseName}
              />
            ) : (
              <p className="font-semibold">{exerciseName}</p>
            )}

            <div className="flex ml-auto gap-1">
              <OrderArrowsComponent />
              {isCoach && (
                <CopyExerciseComponent
                  exercises={activeExercise}
                  athlete={activeUserToHandleCopyAction}
                />
              )}
              <OptionsComponent />
            </div>
          </div>

          {!isFreeForm && (
            <div className="parameters-pb titles">
              {!Array.isArray(activeExercise.parameters) ? (
                <>
                  <span>Sets</span>
                  <span>Reps</span>
                  <span>{amrapLabel}</span>
                  <span>Load</span>
                  {activeExercise.type != "assistant" ? (
                    <span>%1RM</span>
                  ) : null}
                </>
              ) : (
                <>
                  {activeExercise.parameters.map((elem) => (
                    <span
                      className={elem === FREEFORM ? "invisible" : ""}
                      key={`${elem}title`}
                    >
                      {["RPE", "RPE/AMRAP"].includes(elem)
                        ? intensityMeasure
                        : elem}
                    </span>
                  ))}
                </>
              )}
              {isQuickEditOpen && <span className="add-delete-set" />}
            </div>
          )}
        </>
      )}

      <div className="parameters-pb">
        {Array.isArray(activeExercise.parameters) ? (
          <>
            {activeExercise.parameters.map((elem, index) => (
              <span
                className={`${
                  elem === FREEFORM ? "!text-left !basis-3/5" : ""
                }`}
                key={`${index}_${elem}_parameter_${setLength}_${
                  set[mapParameter(elem)]
                }`}
              >
                <span className={valueClassName}>
                  {elem === LOAD
                    ? set.loadDrop
                      ? getLoadDrop(set.loadDrop)
                      : set[mapParameter(elem)]
                      ? withWeight(
                          units === "metric"
                            ? set[mapParameter(elem)]
                            : convert(set[mapParameter(elem)])
                                .from("kg")
                                .to("lb")
                        )
                      : null
                    : ["RPE", RPEAMRAP].includes(elem)
                    ? convertToRiR(set[mapParameter(elem)], !isRiR)
                    : elem === REPS
                    ? repsValue(set.reps, set.repsLower)
                    : set[mapParameter(elem)]}
                </span>

                <ParameterQuickEditInput
                  id={`${exId}_${setIndex}_${elem.toLowerCase()}`}
                  name={mapParameter(elem)}
                  defaultValue={
                    elem === REPS
                      ? repsValue(set.reps, set.repsLower)
                      : set[mapParameter(elem)]
                  }
                  onInput={(e) => handleQuickEditChange(e, exId, setIndex)}
                  parameter={elem}
                  onChange={(e) =>
                    e &&
                    e.target.value &&
                    handleQuickEditChange(e, exId, setIndex)
                  }
                  key={`${exId}_${setIndex}_rpe_${set.rpe}`}
                  units={units}
                  intensityMeasure={intensityMeasure}
                  isRiR={isRiR}
                  className={inputClassName}
                  loadDrop={set.loadDrop}
                />
              </span>
            ))}
          </>
        ) : (
          <>
            <span>
              <span className={valueClassName}>{set.sets}</span>
              <input
                id={`${exId}_${setIndex}_sets`}
                key={`${exId}_${setIndex}_${set.sets}_sets`}
                name="sets"
                defaultValue={set.sets}
                onInput={(e) =>
                  e &&
                  e.target.value &&
                  handleQuickEditChange(e, exId, setIndex)
                }
                className={inputClassName}
                type="number"
                step="1"
              />
            </span>

            <span>
              <span className={valueClassName}>
                {set.rpe === "AMRAP" && !set.reps
                  ? "AMRAP"
                  : repsValue(set.reps, set.repsLower)}
              </span>
              <div className="input">
                <input
                  id={`${exId}_${setIndex}_reps`}
                  key={`${exId}_${setIndex}_${set.reps}_reps`}
                  name="reps"
                  defaultValue={repsValue(set.reps, set.repsLower)}
                  onInput={(e) =>
                    e &&
                    e.target.value &&
                    handleQuickEditChange(e, exId, setIndex)
                  }
                  className={inputClassName}
                  type="text"
                />
              </div>
            </span>

            <span>
              <span className={valueClassName}>
                {convertToRiR(
                  set.rpe === "AMRAP" && !set.reps ? "" : set.rpe,
                  !isRiR
                )}
              </span>
              <select
                id={`${exId}_${setIndex}_rpe`}
                className={inputClassName}
                name="rpe"
                defaultValue={set.rpe}
                onChange={(e) =>
                  e &&
                  e.target.value &&
                  handleQuickEditChange(e, exId, setIndex)
                }
              >
                {
                  // fix for CH1988
                  !set.rpe ? (
                    <option value="">{intensityMeasure}</option>
                  ) : (
                    <option value={set.rpe}>
                      {convertToRiR(set.rpe, !isRiR)}
                    </option>
                  )
                }
                {RPE_RANGE.map((i) => (
                  <option key={i} value={i + 1}>
                    {convertToRiR(i + 1, !isRiR)}
                  </option>
                ))}
                <option value="AMRAP">AMRAP</option>
              </select>
            </span>

            <span
              className={
                set.loadDrop && isQuickEditOpen && !isAssistant
                  ? "!flex-grow-[2]"
                  : ""
              }
            >
              <span className={valueClassName}>
                {set.loadDrop
                  ? getLoadDrop(set.loadDrop)
                  : hasLoad && withWeight(load, units)}
              </span>
              {set.loadDrop ? (
                <input
                  key={`${exId}_${setIndex}_${load}_loadDrop`}
                  name="loadDrop"
                  defaultValue={set.loadDrop}
                  onInput={(e) =>
                    e &&
                    (e.target.value || e.target.value === "") &&
                    handleQuickEditChange(e, exId, setIndex)
                  }
                  className={`${inputClassName} !text-left`}
                  type="text"
                />
              ) : (
                <input
                  id={`${exId}_${setIndex}_load`}
                  key={`${exId}_${setIndex}_${load}_load`}
                  name="load"
                  defaultValue={load}
                  onInput={(e) =>
                    e &&
                    (e.target.value || e.target.value === "") &&
                    handleQuickEditChange(e, exId, setIndex)
                  }
                  className={inputClassName}
                  type="text"
                  ref={(input) =>
                    loadInputs
                      ? (loadInputs[`${exId}_${setIndex}`] = input)
                      : null
                  }
                />
              )}
              <span className={`quick-edit-load-unit ${inputClassName}`}>
                {set.loadDrop ? "% mod." : units === "metric" ? "kg" : "lb"}
              </span>
            </span>

            {!isAssistant && (
              <span className={set.loadDrop && isQuickEditOpen ? "hidden" : ""}>
                <span className={valueClassName}>
                  {!set.loadDrop && set.load
                    ? `${lround(set.percentRepMax, 1)}%`
                    : ""}
                </span>

                <input
                  id={`${exId}_${setIndex}_percentRepMax`}
                  key={`${exId}_${setIndex}__${
                    set.percentRepMax ? lround(set.percentRepMax, 1) : 0
                  }_percentRepMax`}
                  name="percentRepMax"
                  defaultValue={
                    set.percentRepMax ? lround(set.percentRepMax, 1) : 0
                  }
                  onChange={(e) =>
                    e &&
                    e.target.value &&
                    handleQuickEditChange(e, exId, setIndex)
                  }
                  className={`${inputClassName}${
                    set.loadDrop ? " hidden" : ""
                  }`}
                  type="text"
                  ref={(input) =>
                    intensityInputs
                      ? (intensityInputs[`${exId}_${setIndex}`] = input)
                      : null
                  }
                />
                <span
                  className={`quick-edit-load-unit ${inputClassName}${
                    set.loadDrop ? " hidden" : ""
                  }`}
                >
                  %
                </span>
              </span>
            )}
          </>
        )}

        {isQuickEditOpen && (
          <span className={`add-delete-set${isAddingSet ? " invisible" : ""}`}>
            <button data-button-type="secondary" onClick={handleOpenAddSet}>
              <img
                src="/img/new-icons/add-gradient-icon.svg"
                height={24}
                width={24}
              />
            </button>
            <button data-button-type="secondary" onClick={handleDeleteSet}>
              <img src="/img/new-icons/red-trash.svg" height={18} width={18} />
            </button>
          </span>
        )}
      </div>

      {/* This is the line of a set to be added */}
      {isAddingSet &&
        isQuickEditOpen &&
        currentRow === setIndex &&
        (Array.isArray(activeExercise.parameters) ? (
          <NewExerciseLineParameters
            newSet={newSet}
            setIndex={setIndex}
            _handleNewSetChange={_handleNewSetChange}
            exId={exId}
            activeExercise={activeExercise}
            activeUser={activeUserToRequest}
            _handleCloseAddSet={_handleCloseAddSet}
            _handleSaveNewSetForOther={_handleSaveNewSetForOther}
            isProgramEditor={isProgramEditor}
          />
        ) : (
          <div className="parameters-pb">
            <span>
              <input
                autoFocus
                id={`new_${exId}_${setIndex}_sets`}
                name="sets"
                type="number"
                tabIndex={tabIndex++}
                defaultValue={newSet.sets}
                onFocus={handleFocus}
                onInput={(e) =>
                  e && e.target.value && _handleNewSetChange(e, defaultRepMax)
                }
                step="1"
              />
            </span>
            <span>
              <input
                id={`new_${exId}_${setIndex}_reps`}
                name="reps"
                type="text"
                tabIndex={tabIndex++}
                defaultValue={newSet.reps}
                onInput={(e) =>
                  e && e.target.value && _handleNewSetChange(e, defaultRepMax)
                }
              />
            </span>
            <span>
              <select
                id={`new_${exId}_${setIndex}_rpe`}
                name="rpe"
                tabIndex={tabIndex++}
                defaultValue={newSet.rpe}
                onChange={(e) =>
                  e && e.target.value && _handleNewSetChange(e, defaultRepMax)
                }
              >
                <option value="">{intensityMeasure}</option>
                {RPE_RANGE.map((i) => (
                  <option key={i} value={i + 1}>
                    {convertToRiR(i + 1, !isRiR)}
                  </option>
                ))}
                <option value="AMRAP">AMRAP</option>
              </select>
            </span>
            <span>
              <input
                id={`new_${exId}_${setIndex}_load`}
                key={`new_${exId}_${setIndex}_load`}
                name="load"
                type="number"
                tabIndex={tabIndex++}
                defaultValue={newSet.load}
                onInput={(e) =>
                  e && e.target.value && _handleNewSetChange(e, defaultRepMax)
                }
                step=".1"
                ref={(input) =>
                  localLoadInputs ? (localLoadInputs["new"] = input) : null
                }
              />
              <span className={`quick-edit-load-unit ${inputClassName}`}>
                {units === "metric" ? "kg" : "lb"}
              </span>
            </span>
            {!isAssistant && (
              <span>
                <input
                  id={`new_${exId}_${setIndex}_percentRepMax`}
                  key={`new_${exId}_${setIndex}_percentRepMax`}
                  name="percentRepMax"
                  type="number"
                  tabIndex={tabIndex++}
                  defaultValue={newSet.percentRepMax}
                  onChange={(e) =>
                    e && e.target.value && _handleNewSetChange(e, defaultRepMax)
                  }
                  step=".1"
                  ref={(input) =>
                    localIntensityInputs
                      ? (localIntensityInputs["new"] = input)
                      : null
                  }
                />
                <span className={`quick-edit-load-unit ${inputClassName}`}>
                  %
                </span>
              </span>
            )}

            <span className="add-delete-set">
              <button
                data-button-type="primary"
                tabIndex={tabIndex++}
                onClick={(e) =>
                  _handleSaveNewSet(
                    e,
                    set.equipped,
                    defaultRepMax,
                    activeUser,
                    exId,
                    setIndex
                  )
                }
              >
                <SvgIcon src="/img/new-icons/check.svg" noFilterOnLight />
              </button>
              <button
                data-button-type="secondary"
                onClick={(e) => _handleCloseAddSet(e)}
              >
                <SvgIcon
                  src="/img/new-icons/red-cross-20px.svg"
                  noFilterOnLight
                />
              </button>
            </span>
          </div>
        ))}
    </>
  );
};

EditorExerciseLineItem.contextTypes = { locals: PropTypes.object };
export default EditorExerciseLineItem;
